<template>
  <VaDropdown
    :offset="[13, 0]"
    class="notification-dropdown"
    stick-to-edges
    :close-on-content-click="false"
    @update:model-value="onDropdownToggle"
  >
    <template #anchor>
      <VaButton preset="secondary" color="textPrimary">
        <VaBadge overlap :text="badgeText" :color="badgeColor">
          <VaIconNotification class="notification-dropdown__icon" />
        </VaBadge>
      </VaButton>
    </template>
    <VaDropdownContent class="h-full sm:max-w-[420px] sm:h-auto">
      <section class="sm:max-h-[320px] p-4 overflow-auto">
        <div v-if="loading" class="flex justify-center py-4">
          <VaProgressCircle indeterminate size="small" />
        </div>
        <div v-else-if="notifications.length === 0" class="text-center py-4 text-gray-500">No notifications</div>
        <VaList v-else class="space-y-1 mb-2">
          <template v-for="(item, index) in displayedNotifications" :key="item.id">
            <VaListItem class="text-base cursor-pointer hover:bg-gray-100 rounded" @click="handleClick(item)">
              <VaListItemSection icon class="mx-0 p-0">
                <VaIcon :name="item.icon" :color="getIconColor(item)" />
              </VaListItemSection>
              <VaListItemSection>
                {{ item.message }}
              </VaListItemSection>
              <VaListItemSection icon class="mx-1 text-sm text-gray-500">
                {{ formatRelativeTime(item.timestamp) }}
              </VaListItemSection>
            </VaListItem>
            <VaListSeparator v-if="index !== displayedNotifications.length - 1" class="mx-3" />
          </template>
        </VaList>

        <VaButton
          v-if="notifications.length > baseNumberOfVisibleNotifications"
          preset="primary"
          class="w-full"
          @click="displayAllNotifications = !displayAllNotifications"
        >
          {{ displayAllNotifications ? t('notifications.less') : t('notifications.all') }}
        </VaButton>
      </section>
    </VaDropdownContent>
  </VaDropdown>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import VaIconNotification from '../../../icons/VaIconNotification.vue'
import notificationService, { type Notification } from '../../../../services/notification-service'

const { t } = useI18n()
const router = useRouter()

const baseNumberOfVisibleNotifications = 4
const displayAllNotifications = ref(false)
const loading = ref(false)
const notifications = ref<Notification[]>([])
const totalCount = ref(0)
let refreshInterval: ReturnType<typeof setInterval> | null = null

const badgeText = computed(() => {
  if (totalCount.value === 0) return ''
  return totalCount.value > 9 ? '9+' : totalCount.value.toString()
})

const badgeColor = computed(() => {
  const hasHighPriority = notifications.value.some((n) => n.priority === 'high')
  return hasHighPriority ? 'danger' : 'warning'
})

const displayedNotifications = computed(() => {
  return displayAllNotifications.value
    ? notifications.value
    : notifications.value.slice(0, baseNumberOfVisibleNotifications)
})

const getIconColor = (item: Notification) => {
  if (item.priority === 'high') return 'danger'
  if (item.type.includes('approved') || item.icon === 'check_circle') return 'success'
  if (item.type.includes('rejected') || item.icon === 'cancel') return 'danger'
  return 'secondary'
}

const formatRelativeTime = (timestamp: string) => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'now'
  if (diffMins < 60) return `${diffMins}m`
  if (diffHours < 24) return `${diffHours}h`
  if (diffDays < 7) return `${diffDays}d`
  return date.toLocaleDateString()
}

const fetchNotifications = async () => {
  try {
    const response = await notificationService.getNotifications()
    notifications.value = response.data
    totalCount.value = response.total
  } catch (e) {
    console.error('Failed to fetch notifications:', e)
  }
}

const fetchSummary = async () => {
  try {
    const response = await notificationService.getSummary()
    totalCount.value = response.data.total
  } catch (e) {
    console.error('Failed to fetch notification summary:', e)
  }
}

const onDropdownToggle = (isOpen: boolean) => {
  if (isOpen) {
    loading.value = true
    fetchNotifications().finally(() => {
      loading.value = false
    })
  }
}

const handleClick = (item: Notification) => {
  if (item.route) {
    router.push(item.route)
  }
}

onMounted(() => {
  fetchSummary()
  refreshInterval = setInterval(fetchSummary, 60000)
})

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
  }
})
</script>

<style lang="scss" scoped>
.notification-dropdown {
  cursor: pointer;

  .notification-dropdown__icon {
    position: relative;
    display: flex;
    align-items: center;
  }

  .va-dropdown__anchor {
    display: inline-block;
  }
}
</style>
